// Utility functions for parsing and exporting Pokémon Showdown format

import { CalculatorPokemon } from '../hooks/useCalculator';
import { getPokemonByName, getMoveByName, getAllNatures, calculateStat } from './pokemonData';

interface ShowdownPokemon {
  nickname?: string;
  species: string;
  gender?: 'M' | 'F' | '';
  item?: string;
  ability?: string;
  level?: number;
  shiny?: boolean;
  happiness?: number;
  nature?: string;
  evs?: { [key: string]: number };
  ivs?: { [key: string]: number };
  moves: string[];
}

const statAbbreviations: { [key: string]: string } = {
  'HP': 'hp',
  'Atk': 'attack',
  'Def': 'defense',
  'SpA': 'sp_attack',
  'SpD': 'sp_defense',
  'Spe': 'speed'
};

const reverseStatAbbreviations: { [key: string]: string } = {
  'hp': 'HP',
  'attack': 'Atk',
  'defense': 'Def',
  'sp_attack': 'SpA',
  'sp_defense': 'SpD',
  'speed': 'Spe'
};

/**
 * Parse a Pokémon Showdown format string into an array of ShowdownPokemon objects
 */
export const parseShowdownFormat = (text: string): ShowdownPokemon[] => {
  const pokemon: ShowdownPokemon[] = [];

  // Split by double newlines to separate Pokemon
  const blocks = text.split(/\n\s*\n/).filter(block => block.trim());

  for (const block of blocks) {
    const lines = block.split('\n').map(line => line.trim()).filter(line => line);
    if (lines.length === 0) continue;

    const mon: ShowdownPokemon = {
      species: '',
      moves: []
    };

    // Parse first line (name, item, gender)
    const firstLine = lines[0];
    let workingLine = firstLine;

    // Extract item
    const itemMatch = workingLine.match(/@\s*(.+)$/);
    if (itemMatch) {
      mon.item = itemMatch[1].trim();
      workingLine = workingLine.replace(/@\s*.+$/, '').trim();
    }

    // Extract gender
    const genderMatch = workingLine.match(/\(([MF])\)/);
    if (genderMatch) {
      mon.gender = genderMatch[1] as 'M' | 'F';
      workingLine = workingLine.replace(/\([MF]\)/, '').trim();
    }

    // Extract species and nickname
    const speciesMatch = workingLine.match(/^(.+?)\s*\((.+?)\)$/);
    if (speciesMatch) {
      mon.nickname = speciesMatch[1].trim();
      mon.species = speciesMatch[2].trim();
    } else {
      mon.species = workingLine.trim();
    }

    // Parse remaining lines
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];

      // Move
      if (line.startsWith('-')) {
        const move = line.substring(1).trim().split('/')[0].trim();
        // Handle Hidden Power format
        const hpMatch = move.match(/Hidden Power \[?(\w+)\]?/i);
        if (hpMatch) {
          mon.moves.push(`Hidden Power ${hpMatch[1]}`);
        } else {
          mon.moves.push(move);
        }
      }
      // Ability
      else if (line.startsWith('Ability:')) {
        mon.ability = line.substring(8).trim();
      }
      // Level
      else if (line.startsWith('Level:')) {
        mon.level = parseInt(line.substring(6).trim());
      }
      // Shiny
      else if (line.startsWith('Shiny:')) {
        mon.shiny = line.substring(6).trim().toLowerCase() === 'yes';
      }
      // Happiness
      else if (line.startsWith('Happiness:')) {
        mon.happiness = parseInt(line.substring(10).trim());
      }
      // EVs
      else if (line.startsWith('EVs:')) {
        mon.evs = {};
        const evString = line.substring(4).trim();
        const evParts = evString.split('/').map(p => p.trim());
        for (const part of evParts) {
          const match = part.match(/(\d+)\s+(\w+)/);
          if (match) {
            const value = parseInt(match[1]);
            const stat = statAbbreviations[match[2]] || match[2].toLowerCase();
            mon.evs[stat] = value;
          }
        }
      }
      // IVs
      else if (line.startsWith('IVs:')) {
        mon.ivs = {};
        const ivString = line.substring(4).trim();
        const ivParts = ivString.split('/').map(p => p.trim());
        for (const part of ivParts) {
          const match = part.match(/(\d+)\s+(\w+)/);
          if (match) {
            const value = parseInt(match[1]);
            const stat = statAbbreviations[match[2]] || match[2].toLowerCase();
            mon.ivs[stat] = value;
          }
        }
      }
      // Nature
      else if (line.includes('Nature')) {
        mon.nature = line.replace('Nature', '').trim();
      }
    }

    pokemon.push(mon);
  }

  return pokemon;
};

/**
 * Convert a ShowdownPokemon to CalculatorPokemon format
 */
export const showdownToCalculatorPokemon = (showdownMon: ShowdownPokemon): Partial<CalculatorPokemon> => {
  const pokemon = getPokemonByName(showdownMon.species);
  if (!pokemon) {
    throw new Error(`Pokemon "${showdownMon.species}" not found`);
  }

  const nature = getAllNatures().find(n => n.name === showdownMon.nature) || null;

  // Default stats
  const ivs = {
    hp: 31,
    attack: 31,
    defense: 31,
    sp_attack: 31,
    sp_defense: 31,
    speed: 31,
    ...showdownMon.ivs
  };

  const evs = {
    hp: 0,
    attack: 0,
    defense: 0,
    sp_attack: 0,
    sp_defense: 0,
    speed: 0,
    ...showdownMon.evs
  };

  // Convert moves
  const moves = showdownMon.moves.slice(0, 4).map(moveName => {
    const move = getMoveByName(moveName);
    return {
      name: moveName,
      power: move?.power || 0,
      type: move?.type || 'Normal',
      category: move?.category || 'Physical' as 'Physical' | 'Special' | 'Status',
      crit: false,
      zmove: false,
      damage: ''
    };
  });

  // Pad with empty moves if needed
  while (moves.length < 4) {
    moves.push({
      name: '',
      power: 0,
      type: 'Normal',
      category: 'Physical',
      crit: false,
      zmove: false,
      damage: ''
    });
  }

  // Calculate max HP for currentHP
  const level = showdownMon.level || 100;
  const maxHP = calculateStat(pokemon.base_hp, ivs.hp, evs.hp, level, nature, 'hp', true);

  return {
    pokemon,
    level,
    nature,
    ability: showdownMon.ability || '',
    item: showdownMon.item || '(None)',
    gender: showdownMon.gender === 'M' ? 'Male' : showdownMon.gender === 'F' ? 'Female' : 'Genderless',
    ivs,
    evs,
    moves,
    currentHP: {
      current: maxHP,
      total: maxHP,
      percentage: 100
    }
  };
};

/**
 * Export a CalculatorPokemon to Showdown format string
 */
export const calculatorPokemonToShowdown = (mon: CalculatorPokemon): string => {
  if (!mon.pokemon) return '';

  const lines: string[] = [];

  // First line: Name @ Item
  let firstLine = mon.pokemon.name;

  // Add gender if not genderless
  if (mon.gender === 'Male') {
    firstLine += ' (M)';
  } else if (mon.gender === 'Female') {
    firstLine += ' (F)';
  }

  // Add item
  if (mon.item && mon.item !== '(None)') {
    firstLine += ` @ ${mon.item}`;
  }

  lines.push(firstLine);

  // Ability
  if (mon.ability) {
    lines.push(`Ability: ${mon.ability}`);
  }

  // Level (if not 100)
  if (mon.level !== 100) {
    lines.push(`Level: ${mon.level}`);
  }

  // EVs (only include non-zero values)
  const evParts: string[] = [];
  Object.entries(mon.evs).forEach(([stat, value]) => {
    if (value > 0) {
      const abbr = reverseStatAbbreviations[stat] || stat;
      evParts.push(`${value} ${abbr}`);
    }
  });
  if (evParts.length > 0) {
    lines.push(`EVs: ${evParts.join(' / ')}`);
  }

  // IVs (only include non-31 values)
  const ivParts: string[] = [];
  Object.entries(mon.ivs).forEach(([stat, value]) => {
    if (value < 31) {
      const abbr = reverseStatAbbreviations[stat] || stat;
      ivParts.push(`${value} ${abbr}`);
    }
  });
  if (ivParts.length > 0) {
    lines.push(`IVs: ${ivParts.join(' / ')}`);
  }

  // Nature
  if (mon.nature) {
    lines.push(`${mon.nature.name} Nature`);
  }

  // Moves
  mon.moves.forEach(move => {
    if (move.name && move.name !== '(No Move)') {
      lines.push(`- ${move.name}`);
    }
  });

  return lines.join('\n');
};

/**
 * Export both Pokemon from calculator to Showdown format
 */
export const exportBothPokemon = (left: CalculatorPokemon, right: CalculatorPokemon): string => {
  const leftExport = calculatorPokemonToShowdown(left);
  const rightExport = calculatorPokemonToShowdown(right);

  return [leftExport, rightExport].filter(s => s).join('\n\n');
};
